import React, { useState } from 'react';
import { Router, Switch } from 'react-router-dom';

import UIContext from 'components/Utils/UIContext';
import UIProvider from 'components/Utils/UIProvider';
import ModalContext from 'components/Utils/ModalContext';
import ModalProvider from 'components/Utils/ModalProvider';
import SubscriptionProvider from 'components/Utils/SubscriptionProvider';
import SubscriptionContext from 'components/Utils/SubscriptionContext';
import RealtimeContext from 'components/Utils/RealtimeContext';
import { useTokenExpirationMonitor } from './hooks/useTokenExpirationMonitor';
import { useLocationService } from 'services/location';
import { ThemeProvider } from './theme';

import { Layout } from 'antd';
import { Profile } from './pages/profile/Profile';
import { Projects } from './pages/Projects/Projects';
import { Project } from './pages/Project/Project';

import { history } from './utils/history';

import { PrivateRoute as PrivateRouteWithAuth0 } from 'components/navigation/PrivateRoute';
import { PrivateRoute as PrivateRouteWithoutAuth0 } from 'components/navigation/PrivateRouteWithoutAuth0';

import { ThankYou } from 'pages/thankyou/ThankYou';
import { Route } from 'react-router';
import { Agreement } from 'pages/Legal/Agreement';
import { RealtimeProvider } from 'components/Utils/RealtimeProvider';
import { t } from 'i18next';

import 'antd/dist/reset.css';
import './App.sass';

const SelectionConsumer = UIContext.Consumer;
const ModalConsumer = ModalContext.Consumer;
const SubscriptionConsumer = SubscriptionContext.Consumer;
const RealtimeConsumer = RealtimeContext.Consumer;

const PrivateRoute = import.meta.env.VITE_APP_FEATURE_TOKEN_LOGIN === 'enabled'
	? PrivateRouteWithoutAuth0
	: PrivateRouteWithAuth0;

interface Props {
	returnTo: string;
	paymentPublicKey: string;
	auth0Domain?: string;
}

const App: React.FC<Props> = ({ returnTo, paymentPublicKey, auth0Domain }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const location = useLocationService();

	// Ne pas surveiller l'expiration du token sur la page agreement
	const isAgreementPage = location.getPathname() === '/agreement';
	const shouldMonitorToken =
		import.meta.env.VITE_APP_FEATURE_TOKEN_LOGIN !== 'enabled' &&
		!isAgreementPage;
	useTokenExpirationMonitor(shouldMonitorToken);

	return (
		<ThemeProvider>
			<div
				id="App"
			>
				<Router history={history}>
					<Switch>
						<Route
							key="agreement"
							path="/agreement"
							exact={true}
						>
							<Agreement
								auth0Domain={auth0Domain}
								expanded={isExpanded}
								setExpanded={setIsExpanded}
							/>
						</Route>
						<Route path="*">
							<RealtimeProvider>
								<RealtimeConsumer>
									{(realtimeProps) => (
										<SubscriptionProvider>
											<UIProvider>
												<ModalProvider>
													<SubscriptionConsumer>
														{(subscriptionProps) => (
															<SelectionConsumer>
																{(selectionProps) => (
																	<Layout>

																		<Layout.Content>
																			<ModalConsumer>
																				{(modalProps) => (
																					<>
																						{
																							modalProps.modals.map((modal, index: number) => {
																								return React.cloneElement(
																									modal,
																									{
																										key:
																											modal.key ||
																											index,
																										show: true,
																										visible:
																											true,
																										onHide: modalProps.popModal
																									}
																								);
																							})

																						}

																						<Switch>
																							<PrivateRoute
																								key="profile"
																								path="/profile"
																							>
																								<Profile
																									paymentPublicKey={paymentPublicKey}
																									returnTo={returnTo}
																									{...subscriptionProps}
																								/>
																							</PrivateRoute>
																							<PrivateRoute
																								key="project"
																								path="/projects/:id"
																							>
																								<Project
																									{...modalProps}
																									{...selectionProps}
																									{...subscriptionProps}
																									{...realtimeProps}
																									expanded={isExpanded}
																									setExpanded={setIsExpanded}
																								/>
																							</PrivateRoute>
																							<PrivateRoute
																								key="thankyou"
																								path="/thankyou"
																							>
																								<ThankYou
																									{...modalProps}
																									{...subscriptionProps}
																								/>
																							</PrivateRoute>
																							<PrivateRoute
																								key="projectsRoot"
																								exact={true}
																								path=""
																							>
																								<Projects
																									{...modalProps}
																									{...subscriptionProps}
																									{...selectionProps}
																									{...realtimeProps}
																									isFeatureEnabledProfile={!import.meta.env.VITE_APP_FEATURE_TOKEN_LOGIN}
																								/>
																							</PrivateRoute>
																						</Switch>
																					</>
																				)}
																			</ModalConsumer>

																			<div id="copyright" className="noselection">
																				{t('messages.legal.copyright')}
																				<small>
																					{import.meta.env.VITE_APP_MODE} -{' '}
																					{import.meta.env.VITE_APP_ENV}
																				</small>
																			</div>
																		</Layout.Content>
																	</Layout>
																)}
															</SelectionConsumer>
														)}
													</SubscriptionConsumer>
												</ModalProvider>
											</UIProvider>
										</SubscriptionProvider>
									)}
								</RealtimeConsumer>
							</RealtimeProvider>
						</Route>
					</Switch>
				</Router>


			</div>
		</ThemeProvider>
	);
};

export default App;
